import React from 'react';
import { CheckCircle, AlertTriangle, ArrowRight } from 'lucide-react';
import type { HealthItem, HealthIssue } from '../../types';
import { useHealth } from '../../hooks/useHealth';
import { useForecastV3 } from '../../context/ForecastV3Context';
import { MetricCardSkeleton, ListSkeleton } from '../shared/Skeleton';

interface HealthTileProps {
  item: HealthItem;
}

function HealthTile({ item }: HealthTileProps) {
  const Icon = item.status === 'good' ? CheckCircle : AlertTriangle;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon
          size={16}
          className={item.status === 'good' ? 'text-emerald-500' : 'text-amber-500'}
        />
        <span className="text-sm text-slate-600">{item.label}</span>
      </div>
      <div
        className={`text-2xl font-semibold ${
          item.status === 'good' ? 'text-slate-800' : 'text-amber-600'
        }`}
      >
        {item.value}
      </div>
      <div className="text-xs text-slate-400 mt-1">{item.detail}</div>
    </div>
  );
}

interface IssueRowProps {
  issue: HealthIssue;
  onFix?: (issue: HealthIssue) => void;
}

function IssueRow({ issue, onFix }: IssueRowProps) {
  return (
    <div className="px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors">
      <div className="flex items-center gap-3">
        <div
          className={`w-2 h-2 rounded-full flex-shrink-0 ${
            issue.severity === 'high'
              ? 'bg-red-500'
              : issue.severity === 'medium'
              ? 'bg-amber-500'
              : 'bg-slate-400'
          }`}
        />
        <div>
          <div className="font-medium text-slate-800">{issue.account}</div>
          <div className="text-sm text-slate-500">{issue.issue}</div>
        </div>
      </div>
      <button
        onClick={() => onFix?.(issue)}
        className="flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg transition-colors flex-shrink-0"
        style={{ color: 'var(--lodge-orange-500)' }}
        onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--lodge-orange-50)'; }}
        onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = ''; }}
      >
        Fix <ArrowRight size={14} />
      </button>
    </div>
  );
}

export function HealthPage() {
  const { state, setActiveTab, setPendingAccountId, setPendingDetailPanelTab } = useForecastV3();
  const { orgId, locationId, currentVersion, year } = state;

  const {
    healthItems,
    issues,
    overallStatus,
    isLoading,
    error,
  } = useHealth({
    orgId: orgId ?? 0,
    locationId: locationId ?? 0,
    versionId: currentVersion?.id,
    year,
    enabled: !!orgId && !!locationId,
  });

  // Handle "Fix" button - navigate to account in workspace
  const handleFixIssue = (issue: HealthIssue) => {
    if (issue.accountId) {
      setPendingAccountId(issue.accountId);
      setPendingDetailPanelTab('driver');
      setActiveTab('workspace');
    }
  };

  if (isLoading) {
    return (
      <div className="flex-1 p-6 overflow-auto">
        <div className="max-w-6xl mx-auto">
          <div className="h-12 mb-6" /> {/* Header space */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <MetricCardSkeleton />
            <MetricCardSkeleton />
            <MetricCardSkeleton />
            <MetricCardSkeleton />
          </div>
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <ListSkeleton rows={5} />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="p-4 bg-red-50 rounded-xl border border-red-100 text-red-600">
            Error loading health data: {error.message}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 p-6 overflow-auto">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-slate-800">Setup & Health</h2>
            <p className="text-sm text-slate-500 mt-1">
              Monitor data quality and driver coverage
            </p>
          </div>
        </div>

        {/* Health Tiles */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          {healthItems.map((item) => (
            <HealthTile key={item.id} item={item} />
          ))}
        </div>

        {/* Issues List */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
            <h3 className="font-medium text-slate-700">Accounts Needing Attention</h3>
            <span className="text-sm text-slate-500">{issues.length} issues</span>
          </div>
          <div className="divide-y divide-slate-100 max-h-[500px] overflow-y-auto">
            {issues.map((issue) => (
              <IssueRow
                key={issue.id}
                issue={issue}
                onFix={handleFixIssue}
              />
            ))}
            {issues.length === 0 && (
              <div className="p-8 text-center text-slate-500">
                <CheckCircle size={32} className="mx-auto mb-2 text-emerald-500" />
                No issues found. All accounts are properly configured.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default HealthPage;
